import type {
  AgentSessionItem,
  QueuedSessionAction,
  PendingSession,
} from "../session.js";
import type { AutoVoicePreference } from "./types.js";
import type { WorkspaceDescriptor } from "../schemas/index.js";

export type { AgentSessionItem, QueuedSessionAction, PendingSession };
export interface LayoutPreference {
  key: "layout";
  leftSidebarOpen: boolean;
  leftSidebarWidth: number;
  leftSidebarPinnedSectionHeight: number | null;
  rightSidebarOpen: boolean;
  rightSidebarWidth?: number;
  isPreviewOpen: boolean;
  runtimePanelOpen?: boolean;
  runtimePanelHeight?: number;
}

export interface MachineSelectionPreference {
  key: "machine-selection";
  selectedMachineIds: string[];
}

export interface GitDiffPreference {
  key: "git-diff";
  activeTab: "all" | "files";
  viewStyle: "split" | "unified" | "markdown";
}

export interface SidebarCollapsePreference {
  key: "sidebar-collapse";
  workspaces: string[];
  paths: string[];
  worktrees: string[];
}

export type SidebarLayoutGroupBy = "workspace" | "worktree" | "none";
export type SidebarLayoutSortBy = "recency" | "created";
export type SidebarLayoutSortDir = "asc" | "desc";

export interface SidebarLayoutPreference {
  key: "sidebar-layout";
  groupBy: SidebarLayoutGroupBy;
  sortBy: SidebarLayoutSortBy;
  sortDir: SidebarLayoutSortDir;
}

export interface SyncPromptsPreference {
  key: "sync-prompts";
  /** Map of workspaceId to timestamp (ms) when the prompt was shown */
  prompted: Record<string, number>;
}

export interface NewSessionBuilderPreference {
  key: "new-session-builder";
  advancedOpen: boolean;
}

export interface DialogSizePreference {
  key: `dialog-size:${string}`;
  width: number;
  height: number;
}

export type DashboardUIRecord =
  // Persistent dashboard preferences
  | LayoutPreference
  | MachineSelectionPreference
  | GitDiffPreference
  | SidebarCollapsePreference
  | SidebarLayoutPreference
  | SyncPromptsPreference
  | NewSessionBuilderPreference
  | DialogSizePreference
  // Transient state (reset on app startup)
  | { key: "pending-session"; session: PendingSession | null }
  | { key: "new-session-dismissed"; dismissed: boolean }
  | { key: "auto-voice-preference"; pref: AutoVoicePreference | null }
  | { key: "session-overrides"; overrides: Record<string, AgentSessionItem> }
  | { key: "user-settings-modal"; open: boolean; category: string | null }
  | {
      key: "workspace-settings-modal";
      workspace: WorkspaceDescriptor | null;
      path: string | null;
      category: string | null;
    };

export const DASHBOARD_PERSISTENT_DEFAULTS: DashboardUIRecord[] = [
  {
    key: "layout",
    leftSidebarOpen: true,
    leftSidebarWidth: 380,
    leftSidebarPinnedSectionHeight: null,
    rightSidebarOpen: true,
    rightSidebarWidth: 400,
    isPreviewOpen: false,
    runtimePanelOpen: false,
    runtimePanelHeight: 320,
  },
  {
    key: "machine-selection",
    selectedMachineIds: [],
  },
  {
    key: "git-diff",
    activeTab: "all",
    viewStyle: "unified",
  },
  {
    key: "sidebar-collapse",
    workspaces: [],
    paths: [],
    worktrees: [],
  },
  {
    key: "sidebar-layout",
    groupBy: "workspace",
    sortBy: "recency",
    sortDir: "desc",
  },
  {
    key: "sync-prompts",
    prompted: {},
  },
  {
    key: "new-session-builder",
    advancedOpen: false,
  },
];

export const DASHBOARD_TRANSIENT_DEFAULTS: DashboardUIRecord[] = [
  { key: "pending-session", session: null },
  { key: "new-session-dismissed", dismissed: false },
  { key: "auto-voice-preference", pref: null },
  { key: "session-overrides", overrides: {} },
  { key: "user-settings-modal", open: false, category: null },
  {
    key: "workspace-settings-modal",
    workspace: null,
    path: null,
    category: null,
  },
];

export const DASHBOARD_UI_DEFAULTS: DashboardUIRecord[] = [
  ...DASHBOARD_PERSISTENT_DEFAULTS,
  ...DASHBOARD_TRANSIENT_DEFAULTS,
];
